import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const configGeneratorComponent: ComponentDef = {
  id: 'config-generator',
  name: 'Config Generator',
  description: 'Recommend model size, batch size, and learning rate from compute budget and token count.',
  category: 'optimization',
  params: [
    { name: 'budget', label: 'Compute budget (PFLOP·days)', type: 'number', default: 10, min: 0.1, max: 10000, step: 0.1 },
    { name: 'tokens', label: 'Tokens (billions)', type: 'number', default: 100, min: 1, max: 10000, step: 1 },
    { name: 'n_gpus', label: 'GPUs', type: 'number', default: 8, min: 1, max: 1024, step: 1 },
    {
      name: 'gpu',
      label: 'GPU',
      type: 'select',
      default: 'h100',
      options: [
        { label: 'A100-40GB', value: 'a100_40' },
        { label: 'A100-80GB', value: 'a100_80' },
        { label: 'H100', value: 'h100' },
        { label: 'H200', value: 'h200' }
      ]
    }
  ],
  buildGraph: (params) => {
    const budget = Number(params.budget);
    const tokens = Number(params.tokens);
    const n_gpus = Number(params.n_gpus);
    const gpu = String(params.gpu);

    const g = new GraphBuilder();
    g.input('requirements', [tokens, budget]);

    g.scale('chinchilla', 'requirements', 'Chinchilla');

    if (gpu === 'h200') {
      // H200: 141GB HBM3e, 4.8 TB/s bandwidth
      g.scale('gpu_cap', 'requirements', `H200 ×${n_gpus} (141GB)`);
      g.matmul('candidates', 'chinchilla', 'gpu_cap', [tokens, budget]);
      g.scale('d_model', 'candidates', 'd_model');
      g.scale('n_layers', 'candidates', 'n_layers');
      g.scale('lr', 'candidates', 'learning_rate');
      g.scale('hbm3e', 'candidates', 'HBM3e 4.8TB/s');
      g.concat('config', ['d_model', 'n_layers', 'lr', 'hbm3e'], [4]);
    } else if (gpu === 'h100') {
      // H100: 80GB HBM3, 3.35 TB/s bandwidth
      g.scale('gpu_cap', 'requirements', `H100 ×${n_gpus} (80GB)`);
      g.matmul('candidates', 'chinchilla', 'gpu_cap', [tokens, budget]);
      g.scale('d_model', 'candidates', 'd_model');
      g.scale('n_layers', 'candidates', 'n_layers');
      g.scale('lr', 'candidates', 'learning_rate');
      g.scale('hbm3', 'candidates', 'HBM3 3.35TB/s');
      g.concat('config', ['d_model', 'n_layers', 'lr', 'hbm3'], [4]);
    } else if (gpu === 'a100_80') {
      // A100-80GB: 80GB HBM2e, 2.0 TB/s bandwidth
      g.scale('gpu_cap', 'requirements', `A100-80G ×${n_gpus} (80GB)`);
      g.matmul('candidates', 'chinchilla', 'gpu_cap', [tokens, budget]);
      g.scale('d_model', 'candidates', 'd_model');
      g.scale('n_layers', 'candidates', 'n_layers');
      g.scale('lr', 'candidates', 'learning_rate');
      g.scale('hbm2e', 'candidates', 'HBM2e 2.0TB/s');
      g.concat('config', ['d_model', 'n_layers', 'lr', 'hbm2e'], [4]);
    } else {
      // A100-40GB (default): 40GB HBM2e, 1.6 TB/s bandwidth
      g.scale('gpu_cap', 'requirements', `A100-40G ×${n_gpus} (40GB)`);
      g.matmul('candidates', 'chinchilla', 'gpu_cap', [tokens, budget]);
      g.scale('d_model', 'candidates', 'd_model');
      g.scale('n_layers', 'candidates', 'n_layers');
      g.scale('lr', 'candidates', 'learning_rate');
      g.scale('hbm2e', 'candidates', 'HBM2e 1.6TB/s');
      g.concat('config', ['d_model', 'n_layers', 'lr', 'hbm2e'], [4]);
    }

    g.output('out', 'config');
    return g.build();
  }
};
